import {
  Alignment,
  Classes,
  Divider,
  Drawer,
  FormGroup,
  H5,
  Icon,
  Position,
  Switch,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import * as React from 'react';
import { Tooltip } from 'vet-bones/components';

import { InlineEditableLabel } from 'src/components/InlineEditableLabel';
import { TEST_IDS } from 'src/constants/testIds';
import { useRenameMapping } from 'src/containers/ProjectMapping/hooks/useRenameMapping';
import { useUpdateMappingSetClassType } from 'src/containers/ProjectMapping/hooks/useUpdateMappingSetClassType';
import { ProjectMappingUniqueKeySetsMultiSelect } from 'src/containers/ProjectMappingSettingsDrawer/ProjectMappingUniqueKeySetsMultiSelect';
import { projectMappingSelector } from 'src/selectors/projectMapping';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useDesignerPermissions } from 'src/utils/hooks/useDesignerPermissions';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

import styles from 'src/containers/ProjectMappingSettingsDrawer/projectMappingSettingsDrawer.module.scss';

const ProjectMappingSettingsDrawerBody: React.VFC<{ mappingId?: string }> = ({
  mappingId,
}) => {
  const { projectId } = useRouterParams();
  const { canPublishUniqueKeySets } = useDesignerPermissions();
  const mapping = useReduxSelector(
    projectMappingSelector(projectId, mappingId)
  );

  const renameMapping = useRenameMapping(mapping?.key);
  const updateSetClassType = useUpdateMappingSetClassType(mapping?.key);

  const onSetClassType = () => {
    const nextSetClassType = !mapping?.setClassType;
    updateSetClassType(nextSetClassType);
  };

  const mappingName = mapping?.text || '';

  if (!mapping) {
    return null;
  }

  return (
    <div
      className={Classes.DRAWER_BODY}
      data-testid={TEST_IDS.projectMappingSettingsDrawer.container}
    >
      <InlineEditableLabel
        label={copy.components.projectMappingSettingsDrawer.mappingName}
        monospaceText={false}
        onSetValue={renameMapping}
        placeholder={mappingName}
        // setValueOnChange is false, or else it is very laggy to type in
        setValueOnChange={false}
        testId={TEST_IDS.projectMappingSettingsDrawer.mappingName}
        value={mappingName}
      />

      <Divider />
      <H5>{copy.components.projectMappingSettingsDrawer.advancedSettings}</H5>
      <div>
        <Switch
          alignIndicator={Alignment.RIGHT}
          checked={mapping?.setClassType || false}
          data-testid={TEST_IDS.projectMappingSettingsDrawer.setClassType}
          inline
          labelElement={
            <>
              {copy.components.projectMappingSettingsDrawer.setClassType}{' '}
              <Tooltip
                content={
                  copy.components.projectMappingSettingsDrawer.classTypeHelp
                }
              >
                <Icon icon={IconNames.INFO_SIGN} />
              </Tooltip>
            </>
          }
          onChange={onSetClassType}
        />
      </div>
      <FormGroup
        className={styles.uniqueKeySetsFormGroup}
        disabled={!canPublishUniqueKeySets}
        inline
        label={
          <>
            {copy.components.projectMappingSettingsDrawer.uniqueKeySets}{' '}
            <Tooltip
              className={styles.uniqueKeySetsTooltip}
              content={
                copy.components.projectMappingSettingsDrawer.uniqueKeySetsHelp
              }
            >
              <Icon icon={IconNames.INFO_SIGN} />
            </Tooltip>
          </>
        }
      >
        <Tooltip
          content={
            copy.components.projectMappingSettingsDrawer
              .insufficientPrivilegesUniqueKeySets
          }
          disabled={canPublishUniqueKeySets}
          targetTagName="div"
        >
          <ProjectMappingUniqueKeySetsMultiSelect
            disabled={!canPublishUniqueKeySets}
            mapping={mapping}
          />
        </Tooltip>
      </FormGroup>
    </div>
  );
};

export const ProjectMappingSettingsDrawer: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const isOpen = useReduxSelector(
    (state) => state.ui.projectMappingSettingsDrawer.isOpen
  );
  const mappingId = useReduxSelector(
    (state) => state.ui.projectMappingSettingsDrawer.mappingId
  );

  const handleClose = React.useCallback(() => {
    dispatch(
      uiSlice.actions.toggledProjectMappingSettingsDrawer({ isOpen: false })
    );
  }, [dispatch]);

  return (
    <Drawer
      autoFocus={false}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      className="sd-drawer-project-settings"
      enforceFocus={false}
      isOpen={isOpen}
      lazy={false}
      onClose={handleClose}
      position={Position.RIGHT}
      shouldReturnFocusOnClose={false}
      title={copy.components.projectMappingSettingsDrawer.title}
    >
      <ProjectMappingSettingsDrawerBody mappingId={mappingId} />
    </Drawer>
  );
};
